import DateRangeFilter, { DateRangeFilterChange } from "@/components/filters/DateRangeFilter";
import { Agent, useAppData } from "@/contexts/AppDataContext";
import { calculateDateRange, DATE_RANGE_KEYS } from "@/utils/dateRangeUtils";
import React, { useEffect } from "react";
import { AgentFilter } from "../filters/AgentFilter";
import { CallQueueFilter } from "../filters/CallQueueFilter";
import { DirectAgentFilter } from "../filters/DirectAgentFilter";
import { QueueFilter } from "../filters/QueueFilter";
import { CallQueueType, EMPTY_AGENT_ID } from "@/constants/queue";
import { SectionType } from "@/constants/section";
import { FilterBarProps } from "@/types";

const FilterBar: React.FC<FilterBarProps> = ({
  onFilterChange,
  initialDateRangeKey = DATE_RANGE_KEYS.LAST_30_DAYS,
  initialSelectedQueues = [],
  initialSelectedAgents = [],
  initialSelectedQueueType = CallQueueType.CALL_QUEUE,
  initialDateRange,
  quickRanges,
  className,
  sectionType,
}) => {
  const { queuesResponse, uniqueAgents, directAgentGroups, loaded: appDataLoading } = useAppData();
  const [dateRangeKey, setDateRangeKey] = React.useState<string>(initialDateRangeKey);
  const [selectedQueue, setSelectedQueue] = React.useState<string[]>(initialSelectedQueues);
  const [selectedAgents, setSelectedAgents] = React.useState<string[]>(initialSelectedAgents);
  const [selectedQueueType, setSelectedQueueType] = React.useState<CallQueueType | null>(initialSelectedQueueType);
  const [dateRange, setDateRange] = React.useState<{
    startDate: Date;
    endDate: Date;
    previousStartDate: Date;
    previousEndDate: Date;
  }>(() => {
    const range = calculateDateRange(initialDateRangeKey);
    return {
      startDate: range.from,
      endDate: range.to,
      previousStartDate: range.previousFrom,
      previousEndDate: range.previousTo,
    };
  });
  const prevQueuesRef = React.useRef<typeof queuesResponse>(queuesResponse);
  const selectedQueueRef = React.useRef(selectedQueue);
  selectedQueueRef.current = selectedQueue;
  const selectedAgentsRef = React.useRef(selectedAgents);
  selectedAgentsRef.current = selectedAgents;

  // Get available agents based on selected queues
  const availableAgents = React.useMemo(() => {
    if (selectedQueue.length === 0) {
      // If no queue is selected, return all agents
      return uniqueAgents.filter(agent => agent.queueType === selectedQueueType);
    }
    // Return agents that belong to selected queues
    const queueAgents = queuesResponse
      .filter(queue => selectedQueue.includes(queue.type === CallQueueType.CALL_QUEUE ? queue.microsoftId : queue.id?.toString()))
      .flatMap(queue => queue.agents || []);

    return Array.from(
      new Map(queueAgents.map((agent) => [agent.microsoftId, agent])).values()
    );
  }, [queuesResponse, selectedQueue, appDataLoading, selectedQueueType]);

  // When queuesResponse changes, reset only selections that are no longer valid
  useEffect(() => {
    const prev = prevQueuesRef.current;
    prevQueuesRef.current = queuesResponse;
    // Skip initial mount and initial data load (empty → populated)
    if (prev === queuesResponse || prev.length === 0) return;

    const validQueueIds = new Set(
      queuesResponse.map(q => q.type === CallQueueType.CALL_QUEUE ? q.microsoftId : q.id?.toString())
    );
    const validAgentIds = new Set(
      queuesResponse.flatMap(q => (q.agents ?? []).map(a => a.microsoftId))
    );
    const currentQueues = selectedQueueRef.current;
    const currentAgents = selectedAgentsRef.current;
    const newQueues = currentQueues.filter(id => validQueueIds.has(id));
    const newAgents = currentAgents.filter(id => validAgentIds.has(id));

    if (newQueues.length === currentQueues.length && newAgents.length === currentAgents.length) {
      return; // All selections still valid in new queuesResponse
    }

    setSelectedQueue(newQueues);
    setSelectedAgents(newAgents);

    let newSelectedQueueType = selectedQueueType;
    if ((sectionType == SectionType.INSTANT_CONNECT_INSIGHT || sectionType == SectionType.DIRECT_CALL) &&
      selectedQueueType == CallQueueType.DIRECT_AGENT_GROUP &&
      (directAgentGroups || []).length === 0
    ) {
      newSelectedQueueType = CallQueueType.AGENT;
      setSelectedQueueType(newSelectedQueueType);
    }

    onFilterChange({
      dateRange: {
        key: dateRangeKey,
        ...dateRange
      },
      queues: newQueues,
      agents: newAgents,
      selectedQueueType: newSelectedQueueType
    });

  }, [queuesResponse]);

  // Trigger initial date range calculation
  useEffect(() => {
    const range = initialDateRange?.key === DATE_RANGE_KEYS.CUSTOM
      ? {
        from: new Date(initialDateRange?.startDate),
        to: new Date(initialDateRange?.endDate),
        previousFrom: new Date(initialDateRange?.previousStartDate),
        previousTo: new Date(initialDateRange?.previousEndDate),
      }
      : calculateDateRange(initialDateRange?.key);

    setDateRange({
      startDate: range?.from,
      endDate: range?.to,
      previousStartDate: range?.previousFrom,
      previousEndDate: range?.previousTo,
    });
  }, [initialDateRange]);

  const handleDateRangeChange = (range: DateRangeFilterChange) => {
    setDateRangeKey(range.key);
    setDateRange({
      startDate: range.startDate,
      endDate: range.endDate,
      previousStartDate: range.previousStartDate,
      previousEndDate: range.previousEndDate,
    });
    onFilterChange({
      dateRange: {
        key: range.key,
        startDate: range.startDate,
        endDate: range.endDate,
        previousStartDate: range.previousStartDate,
        previousEndDate: range.previousEndDate,
      },
      queues: selectedQueue,
      agents: selectedAgents,
      selectedQueueType
    });
  };

  const handleQueueChange = (queues: string[], callQueueType?: CallQueueType) => {
    setSelectedQueue(queues);
    const queueType = callQueueType || selectedQueueType;
    setSelectedQueueType(queueType);
    // Reset selected agents when queues change
    setSelectedAgents([]);
    onFilterChange({
      dateRange: {
        key: dateRangeKey,
        ...dateRange
      },
      queues,
      agents: [],
      selectedQueueType: queueType
    });
  };

  const handleAgentChange = (agents: string[]) => {
    setSelectedAgents(agents);
    onFilterChange({
      dateRange: {
        key: dateRangeKey,
        ...dateRange
      },
      queues: selectedQueue,
      agents,
      selectedQueueType
    });
  };

  const handleDirectCallAgentChange = (values: string[], filterType: CallQueueType) => {
    setSelectedQueueType(filterType);

    if (filterType === CallQueueType.DIRECT_AGENT_GROUP) {
      const rawExpandedAgentIds = Array.from(
        new Set(
          directAgentGroups
            .filter(group => values.includes(group.microsoftId))
            .flatMap(group => group.agentMicrosoftIds)
        )
      );
      // Selected groups (not "All Groups") with no member agents must still filter
      // results down to nothing, rather than an empty agentIds array being read
      // downstream as "no filter applied" and returning all agents' data.
      const expandedAgentIds = values.length > 0 && rawExpandedAgentIds.length === 0
        ? [EMPTY_AGENT_ID]
        : rawExpandedAgentIds;
      setSelectedQueue(values);
      setSelectedAgents(expandedAgentIds);
      onFilterChange({
        dateRange: {
          key: dateRangeKey,
          ...dateRange
        },
        queues: values,
        agents: expandedAgentIds,
        selectedQueueType: filterType
      });
    } else if (filterType === CallQueueType.AGENT) {
      setSelectedAgents(values);
      setSelectedQueue([]);
      onFilterChange({
        dateRange: {
          key: dateRangeKey,
          ...dateRange
        },
        queues: [],
        agents: values,
        selectedQueueType: filterType
      });
    }
  };

  const handleDirectAgentChange = (values: string[], filterType: CallQueueType) => {
    setSelectedQueueType(filterType);

    if (filterType === CallQueueType.DIRECT_AGENT_GROUP) {
      // Update queues for direct agent groups
      setSelectedQueue(values);
      setSelectedAgents([]); // Clear agents when selecting groups
      onFilterChange({
        dateRange: {
          key: dateRangeKey,
          ...dateRange
        },
        queues: values,
        agents: [],
        selectedQueueType: filterType
      });
    } else if (filterType === CallQueueType.AGENT) {
      // Update agents for individual agents
      setSelectedAgents(values);
      setSelectedQueue([]); // Clear queues when selecting agents
      onFilterChange({
        dateRange: {
          key: dateRangeKey,
          ...dateRange
        },
        queues: [],
        agents: values,
        selectedQueueType: filterType
      });
    }
  };

  const renderFilters = () => {
    switch (sectionType) {
      case SectionType.CALL_OVERVIEW:
        return (
          <>
            <DateRangeFilter
              value={dateRangeKey}
              onChange={handleDateRangeChange}
              quickRanges={quickRanges}
              initialDateRange={initialDateRange}
              className={className}
            />
            <QueueFilter
              value={selectedQueue}
              onChange={(queues) => handleQueueChange(queues, CallQueueType.CALL_QUEUE)}
              className={className}
            />
          </>
        );

      case SectionType.INSTANT_CONNECT_INSIGHT:
        return (
          <>
            <DateRangeFilter
              value={dateRangeKey}
              onChange={handleDateRangeChange}
              quickRanges={quickRanges}
              initialDateRange={initialDateRange}
              className={className}
            />
            <DirectAgentFilter
              value={selectedQueueType === CallQueueType.AGENT ? selectedAgents : selectedQueue}
              filterType={selectedQueueType !== CallQueueType.CALL_QUEUE ? selectedQueueType : CallQueueType.DIRECT_AGENT_GROUP}
              onChange={handleDirectAgentChange}
              className={className}
            />
          </>
        );

      case SectionType.DIRECT_CALL:
        return (
          <>
            <DateRangeFilter
              value={dateRangeKey}
              onChange={handleDateRangeChange}
              quickRanges={quickRanges}
              initialDateRange={initialDateRange}
              className={className}
            />
            <DirectAgentFilter
              value={selectedQueueType === CallQueueType.AGENT ? selectedAgents : selectedQueue}
              filterType={selectedQueueType !== CallQueueType.CALL_QUEUE ? selectedQueueType : CallQueueType.DIRECT_AGENT_GROUP}
              onChange={handleDirectCallAgentChange}
              agents={uniqueAgents}
              className={className}
            />
          </>
        );

      case SectionType.TRENDS:
        return (
          <DateRangeFilter
            value={dateRangeKey}
            onChange={handleDateRangeChange}
            quickRanges={quickRanges}
            initialDateRange={initialDateRange}
            className={className}
          />
        );

      default:
        return (
          <>
            <DateRangeFilter
              value={dateRangeKey}
              onChange={handleDateRangeChange}
              quickRanges={quickRanges}
              initialDateRange={initialDateRange}
              className={className}
            />
            {/* <CallQueueFilter
              value={selectedQueue}
              onChange={(queues) => handleQueueChange(queues)}
              queueType={selectedQueueType}
              className={className}
            /> */}
            <AgentFilter
              value={selectedAgents}
              onChange={handleAgentChange}
              availableAgents={availableAgents}
              className={className}
            />
          </>
        );
    }
  };

  return (
    <div className="mb-1 flex flex-col md:flex-row align-center gap-2">
      {renderFilters()}
    </div>
  );
};

export default FilterBar;
